import { CropDialog, type Frame } from "@countdown/door-editor";
import { type ReactElement, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "@/components/ui/button";
import { originalAddress } from "@/lib/api";

/** What a new cut answered: the page refreshes on success, a refusal stays under the button. */
export type CropOutcome = { readonly ok: true } | { readonly ok: false; readonly message: string };

/**
 * "Zuschneiden" for one stored picture outside a door (D44): a logo, the placeholder, a
 * partner's logo. The dialog of the door editor, over the page; the frame starts free.
 */
export function CropButton({
  assetId,
  alt,
  label,
  text = "Zuschneiden",
  primary = false,
  disabled = false,
  onApply,
}: {
  /** The file the place shows; the server cuts its original. */
  readonly assetId: string;
  readonly alt: string;
  /** The button's name for screen readers, with its place: "Logo zuschneiden". */
  readonly label: string;
  /** The visible words: »Logo prüfen« where the cut is the check of a waiting logo (D94). */
  readonly text?: string;
  /** The page's one step, filled rather than outlined. */
  readonly primary?: boolean;
  readonly disabled?: boolean;
  readonly onApply: (frame: Frame) => Promise<CropOutcome>;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const button = useRef<HTMLButtonElement>(null);

  async function apply(frame: Frame): Promise<void> {
    setBusy(true);
    setRefused(null);
    try {
      const outcome = await onApply(frame);
      if (!outcome.ok) {
        setRefused(outcome.message);
      }
    } catch (error: unknown) {
      console.error("[CropButton] cut failed", error);
      setRefused("Der Schritt konnte nicht ausgeführt werden.");
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  return (
    <>
      <Button
        ref={button}
        type="button"
        variant={primary ? "default" : "outline"}
        size="sm"
        className="w-max"
        disabled={disabled || busy}
        aria-label={label}
        onClick={() => {
          setRefused(null);
          setOpen(true);
        }}
      >
        {text}
      </Button>
      {refused !== null ? (
        <p className="text-sm text-destructive" role="alert">
          {refused}
        </p>
      ) : null}
      {open
        ? createPortal(
            <CropDialog
              src={originalAddress(assetId)}
              alt={alt}
              initial={null}
              ratio="free"
              busy={busy}
              onApply={(frame) => {
                void apply(frame);
              }}
              onCancel={() => {
                setOpen(false);
              }}
            />,
            portalTarget(button.current),
          )
        : null}
    </>
  );
}

/**
 * Where a dialog over the page goes: into an open modal dialog around `node` if there is one (the
 * door's dialog, D94), else the body. Outside the modal it would be inert to pointer and focus.
 */
export function portalTarget(node: Element | null): Element {
  return node?.closest('[role="dialog"]') ?? document.body;
}

/** The id of a stored file from its media path, `<uuid>.<ext>`. */
export function assetIdOf(path: string): string {
  return path.split(".")[0] ?? path;
}
